import { Tooltip } from "antd"
import { ReactComponent as OrthogonalSvg } from './icons/orthogonal.svg';
import { ReactComponent as OrthogonalWhiteSvg } from './icons/orthogonal-white.svg';
import { ReactComponent as PolarSvg } from './icons/polar.svg';
import { ReactComponent as PolarWhiteSvg } from './icons/polar-white.svg';
import { ReactComponent as DymodeSvg } from './icons/dymode.svg';
import { ReactComponent as DymodeWhiteSvg } from './icons/dymode-white.svg';
import { ReactComponent as CaptureSvg } from './icons/capture.svg';
import { ReactComponent as CaptureWhiteSvg } from './icons/capture-white.svg';

import { ReactComponent as UpArrowSvg } from './icons/up-arrow.svg';
import { ReactComponent as UpArrowWhiteSvg } from './icons/up-arrow-white.svg';
import { ReactComponent as DownArrowSvg } from './icons/down-arrow.svg';
import { ReactComponent as DownArrowWhiteSvg } from './icons/down-arrow-white.svg';

import Icon, { CheckOutlined, SettingOutlined } from '@ant-design/icons';
import './index.less'
import { useContext, useEffect, useState } from "react";
import { EngineContext } from "@/App";
import { PythaEventSetting, RenderMode, SETTING_KEY } from 'pytha2';

import { NumFixed } from "@/utils/num-fixed";
import { ContextMenu, Modal } from "bimui";
import BottomSetting from "./BottomSetting";
import classNames from "classnames";
import { emitter } from "@/utils/mitt";
import { Emit_Event_Common_Tool } from "@/declare/enums/emit-event";
import { FEATURE_POINT_TYPE } from "tnbase";
import { Transfer2DisplayScale } from "@/utils/unit";

export interface captureMode {
    number?: number;
    name?: string;
    icon?: any;
    active?: boolean;
}

// 假数据：捕捉模式列表
const initialCaptureModes: captureMode[] = [
    { number: FEATURE_POINT_TYPE.END, name: '端点', active: true, icon: SettingOutlined },
    { number: FEATURE_POINT_TYPE.MIDDLE, name: '中点', active: true, icon: SettingOutlined },
    // { number: 3, name: '垂足', active: false, icon: SettingOutlined },
    // { number: 4, name: '插入点', active: false, icon: SettingOutlined },
    // { number: 5, name: '节点', active: false, icon: SettingOutlined },
    { number: FEATURE_POINT_TYPE.CENTER, name: '中心点', active: false, icon: SettingOutlined },
    { number: FEATURE_POINT_TYPE.QUADRANTAL, name: '象限点', active: false, icon: SettingOutlined },
    // { number: 8, name: '最近点', active: false, icon: SettingOutlined },
    { number: FEATURE_POINT_TYPE.INTERSECTION, name: '交点', active: false, icon: SettingOutlined },
    // { number: 10, name: '切点', active: false, icon: SettingOutlined },
];

/**
 * `SystemSwitch` 组件
 * 底部工具区
 */
export default function SystemSwitch() {
    /**
     * 是否显示设置弹窗（对象捕捉）
     */
    const [bottomModalVisible, setBottomModalVisible] = useState(false);
    /**
     * 展示弹窗的页签（默认为对象捕捉设置）
     */
    const [tabPane, setTabPane] = useState('capture');


    /**
     * 是否开启动态lod
     */
    const [lod, setLod] = useState<boolean>(false);

    /**
     * 是否显示对象捕捉菜单
     */
    const [captureMenuVisible, setCaptureMenuVisible] = useState(false);
    /**
     * 对象捕捉菜单水平位置
     */
    const [captureMenuVisibleLeft, setCaptureMenuVisibleLeft] = useState(0);
    /**
     * 对象捕捉菜单数据
     */
    const [captureModes, setCaptureModes] = useState<captureMode[]>(initialCaptureModes);
    /**
     * 是否启用正交模式
     */
    const [orthogonal, setOrthogonal] = useState(false);
    /**
     * 是否启用自动捕捉
     */
    const [autoSnap, setAutoSnap] = useState(false);
    /**
     * 是否启用动态输入模式
     */
    const [dymode, setDymode] = useState(false);
    /**
     * 是否启用对象捕捉
     */
    const [capture, setCapture] = useState(false);
    const { engine, unit, setUnit } = useContext(EngineContext); // 获取上下文

    const [ucsX, setUcsX] = useState<number>(0);
    const [ucsY, setUcsY] = useState<number>(0);
    const [ucsZ, setUcsZ] = useState<number>(0);
    const [length, setLength] = useState<number>(0); //长度

    const [renderMode, setRenderMode] = useState<number>();

    /**
     * 引擎初始化后执行
     */
    useEffect(() => {
        if (!engine) return;
        const root = document.getElementById('viewport');
        if (!root) return;
        setCapture(engine.storage?.captureStore?.enabled);
        setOrthogonal(engine.storage?.orthoPolarStore?.orthoEnabled);
        setRenderMode(engine.storage?.renderStore?.renderMode);

        root.addEventListener('pointermove', onPointerMove);
        document.addEventListener('keydown', handleKeyDown);
        return (() => {
            document.removeEventListener('keydown', handleKeyDown);
            root.removeEventListener('pointermove', onPointerMove);
        })
    }, [engine])

    useEffect(() => {
        emitter.on(Emit_Event_Common_Tool.RENDER_MODE_CHANGE, onListenRenderModeChange)

        return () => {
            emitter.off(Emit_Event_Common_Tool.RENDER_MODE_CHANGE, onListenRenderModeChange)
        }
    }, [])

    useEffect(() => {
        onPointerMove(null);
    }, [unit])

    // 监听其他组件修改渲染模式
    const onListenRenderModeChange = (renderMode: number) => {
        setRenderMode(renderMode);
    }

    /**
     * 设置光标坐标值实时显示
     * @param event 
     */
    const onPointerMove = (event) => {
        if (!engine) return;
        const unitRatio = engine.storage?.coordinateStore.lengthUnitRatio || 1;
        setUcsX((engine.storage?.coordinateStore?.currentUcsPos?.x || 0) / unitRatio);
        setUcsY((engine.storage?.coordinateStore?.currentUcsPos?.y || 0) / unitRatio);
        setUcsZ((engine.storage?.coordinateStore?.currentUcsPos?.z || 0) / unitRatio);
        setLength((engine.storage?.coordinateStore?.showLength || 0) / unitRatio);
    }

    /**
     * 状态栏快捷键设置属性 捕捉、正交、极轴等
     * @param event 
     * @returns 
     */
    const handleKeyDown = (event) => {
        // event.preventDefault();
        if (!engine) return;
        if (event.code === "F3") {
            // 捕捉
            let pythaEvent = new PythaEventSetting();
            let value = !engine.storage.captureStore.enabled;
            pythaEvent.data = { key: SETTING_KEY.CAPTURE, value: value }
            engine.eventMediator?.emitEvent(pythaEvent);
            setCapture(value);
            event.preventDefault();
            return;
        }
        if (event.code === "F8") {
            // 正交
            let pythaEvent = new PythaEventSetting();
            let value = !engine.storage.orthoPolarStore.orthoEnabled;
            pythaEvent.data = { key: SETTING_KEY.ORTHA, value: value }
            engine.eventMediator?.emitEvent(pythaEvent);
            setOrthogonal(value);
            setAutoSnap(engine.storage.orthoPolarStore.polarEnabled);
            event.preventDefault();
            return;
        }
        if (event.code === "F10") {
            // 极轴
            let pythaEvent = new PythaEventSetting();
            let value = !engine.storage.orthoPolarStore.polarEnabled;
            pythaEvent.data = { key: SETTING_KEY.POLAR, value: value }
            engine.eventMediator?.emitEvent(pythaEvent);
            setAutoSnap(value);
            setOrthogonal(engine.storage.orthoPolarStore.orthoEnabled);
            event.preventDefault();
            return;
        }
    };

    const handleMouseEnterOrthogonal = () => { };
    const handleMouseLeaveOrthogonal = () => { };
    const handleMouseEnterPolar = () => { };
    const handleMouseLeavePolar = () => { };
    const handleMouseEnterDymode = () => { };
    const handleMouseLeaveDymode = () => { };

    /**
     * 增加指定捕捉模式的激活状态
     * 
     * @param {number} number - 捕捉模式的唯一标识符（number）。
     */
    const toggleCaptureMode = (number: number) => {
        const updatedModes = captureModes.map(mode => {
            if (mode.number === number) {
                return { ...mode, active: !mode.active }; // 切换激活状态
            }
            return mode;
        });

        let event = new PythaEventSetting();
        let activeTypeList = []
        updatedModes.forEach(mode => {
            if (mode.active) {
                activeTypeList.push(mode.number)
            }
        })
        event.data = {
            key: SETTING_KEY.CAPTURE_TYPE_LIST,
            value: activeTypeList,
        }
        engine.eventMediator?.emitEvent(event);
        setCaptureModes(updatedModes);
    };

    /**
     * 激活对象捕捉状态的菜单栏渲染内容
     */
    const CaptureMenu = (
        <div className="capture-menu-container">
            {captureModes.map((item, idx) => (
                <div className="capture-menu-item" key={item.number} onClick={() => toggleCaptureMode(item.number)}>
                    <div className="check-icon">
                        {item.active && (<CheckOutlined style={{ color: 'white' }} />)}
                    </div>
                    <div>
                        <Icon style={{ margin: '5px', fontSize: '16px' }} />
                    </div>
                    <div>
                        {item.name}
                    </div>
                </div>
            ))}
            <div className="menu-divider"></div>
            <div className="capture-menu-item setting" onClick={() => {
                setCaptureMenuVisible(false);
                setTabPane('capture');
            }}>
                <div className="check-icon">
                </div>
                <span onClick={() => setBottomModalVisible(true)}>对象捕捉设置...</span>
            </div>
        </div>
    )
    /**
     * 设置对象捕捉状态的鼠标悬浮状态
     */
    const [isCaptureHovered, setIsCaptureHovered] = useState(false);
    /**
     * 处理鼠标进入对象捕捉按钮事件
     */
    const handleMouseEnterCapture = () => {
        setIsCaptureHovered(true);
    };
    /**
     * 处理鼠标离开对象捕捉按钮事件
     */
    const handleMouseLeaveCapture = () => {
        setIsCaptureHovered(false);
    };

    /**
     * 设置渲染模式的变更
     * @param value 
     * @returns 
     */
    const onRenderModeChange = (value: number) => {
        if (!engine) return;
        if (engine?.storage.renderStore.renderMode == value) return;
        setRenderMode(value);
        let event = new PythaEventSetting();
        event.data = {
            key: SETTING_KEY.RENDER_MODE,
            value: value,
        }
        engine.eventMediator?.emitEvent(event);
    }

    useEffect(() => {

    }, [engine])


    /**
     * 当lod状态修改时，对应修改库里的lod状态
     */
    useEffect(() => {
        if (engine) {
            if (engine.eventMediator?.storage?.renderStore) {
                engine.eventMediator.storage.renderStore.lodEnabled = lod;
            }
        }
    }, [lod])

    return (
        <div className="system-switch">

            <div className="model-type-select">
                <div className={classNames({
                    "option-item": true,
                    "active": renderMode === RenderMode.CONCEPT
                })} onClick={() => {
                    onRenderModeChange(RenderMode.CONCEPT);
                }}>实体</div>
                <div className={classNames({
                    "option-item": true,
                    "active": renderMode === RenderMode.OPACITY
                })} onClick={() => {
                    onRenderModeChange(RenderMode.OPACITY);
                }}>透明</div>
            </div>
            <div className="coord">

                {/* <span
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: 'auto',
                        cursor: 'pointer',
                        height: 26,
                    }}
                    onClick={(e) => {
                        setRenderMenuVisible(true);
                        setRenderMenuVisibleLeft(e.clientX + 40);
                    }}
                >
                    {systemContext.currentRenderMode === 'WIREFRAME' && `线框模型`}
                    {systemContext.currentRenderMode === 'CONCEPT' && `概念模型`}
                    {!renderMenuVisible && <DownArrowSvg style={{ marginLeft: 3 }} />}
                    {renderMenuVisible && <UpArrowSvg style={{ marginLeft: 3 }} />}
                </span> */}

                <Tooltip title="图形坐标" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <span>X={NumFixed(ucsX, 4)}, </span>
                    <span>Y={NumFixed(ucsY, 4)}, </span>
                    <span>Z={NumFixed(ucsZ, 4)}</span>
                    <span> 长度={NumFixed(length, 4)}</span>
                </Tooltip>
            </div>
            <div className="switch-group">

                <Tooltip title="正交(F8)" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <div
                        className={classNames({
                            "system-ope": true,
                            "active": orthogonal,
                        })}
                        onClick={() => {
                            let pythaEvent = new PythaEventSetting();
                            let value = !engine.storage.orthoPolarStore.orthoEnabled;
                            pythaEvent.data = { key: SETTING_KEY.ORTHA, value: value }
                            engine?.eventMediator?.emitEvent(pythaEvent);
                            setOrthogonal(value);
                            setAutoSnap(engine.storage.orthoPolarStore.polarEnabled);
                        }}
                        onMouseEnter={handleMouseEnterOrthogonal}
                        onMouseLeave={handleMouseLeaveOrthogonal}
                    >
                        {!orthogonal ? <OrthogonalSvg /> : <OrthogonalWhiteSvg />}
                    </div>
                </Tooltip>

                {/*  极轴追踪 */}
                <Tooltip title="极轴追踪(F10)" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <div style={{ height: "26px", display: 'flex', alignItems: 'center', backgroundColor: !autoSnap ? "#fff" : "#11788f" }}>
                        <div
                            className="system-ope"
                            onClick={() => setAutoSnap(!autoSnap)}
                            onMouseEnter={handleMouseEnterPolar}
                            onMouseLeave={handleMouseLeavePolar}
                        >
                            {!autoSnap ? <PolarSvg /> : <PolarWhiteSvg />}
                        </div>
                        <div className="system-triangle" onClick={(e) => { }}
                            onMouseEnter={handleMouseEnterPolar}
                            onMouseLeave={handleMouseLeavePolar}
                        >
                            {autoSnap && <UpArrowWhiteSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                            {!autoSnap && <UpArrowSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                        </div>
                    </div>
                </Tooltip>

                {/*  动态输入 */}
                {/* <Tooltip title="动态输入" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <div style={{ height: "26px", display: 'flex', alignItems: 'center', backgroundColor: !dymode ? "#fff" : "#11788f" }}>
                        <div
                            className="system-ope"
                            onClick={() => setDymode(!dymode)}
                            onMouseEnter={handleMouseEnterDymode}
                            onMouseLeave={handleMouseLeaveDymode}
                        >
                            {!dymode ? <DymodeSvg /> : <DymodeWhiteSvg />}
                        </div>
                    </div>
                </Tooltip> */}

                {/*  对象捕捉 */}
                <Tooltip title="对象捕捉(F3)" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    {capture &&
                        <div style={{ height: "26px", display: 'flex', alignItems: 'center', backgroundColor: isCaptureHovered ? "#d3e2e5" : "#11788f" }}>
                            <div
                                className="system-ope"
                                onClick={() => {
                                    let pythaEvent = new PythaEventSetting();
                                    let value = !capture
                                    pythaEvent.data = { key: SETTING_KEY.CAPTURE, value: value }
                                    engine.eventMediator?.emitEvent(pythaEvent);
                                    setCapture(value);
                                    setCapture(!capture)
                                }}
                                onMouseEnter={handleMouseEnterCapture}
                                onMouseLeave={handleMouseLeaveCapture}
                            >
                                {isCaptureHovered ? <CaptureSvg /> : <CaptureWhiteSvg />}
                            </div>
                            <div className="system-triangle" onClick={(e) => {
                                setCaptureMenuVisibleLeft(e.clientX); setCaptureMenuVisible(true)
                            }}
                                onMouseEnter={handleMouseEnterCapture}
                                onMouseLeave={handleMouseLeaveCapture}
                            >
                                {isCaptureHovered && captureMenuVisible && <UpArrowSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                                {!isCaptureHovered && captureMenuVisible && <UpArrowWhiteSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                                {isCaptureHovered && !captureMenuVisible && <DownArrowSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                                {!isCaptureHovered && !captureMenuVisible && <DownArrowWhiteSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                            </div>
                        </div>
                    }
                    {!capture &&
                        <div style={{ height: "26px", display: 'flex', alignItems: 'center', backgroundColor: isCaptureHovered ? "#d3e2e5" : "#FFFFFF" }}>
                            <div
                                className="system-ope"
                                onClick={() => { setCapture(!capture) }}
                                onMouseEnter={handleMouseEnterCapture}
                                onMouseLeave={handleMouseLeaveCapture}
                            >
                                <CaptureSvg />
                            </div>
                            <div className="system-triangle" onClick={(e) => {
                                setCaptureMenuVisibleLeft(e.clientX); setCaptureMenuVisible(true)
                            }}
                                onMouseEnter={handleMouseEnterCapture}
                                onMouseLeave={handleMouseLeaveCapture}
                            >
                                {captureMenuVisible && <UpArrowSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                                {!captureMenuVisible && <DownArrowSvg style={{ paddingTop: '0px', display: 'block', cursor: 'pointer' }} />}
                            </div>
                        </div>
                    }
                </Tooltip>
                <Tooltip title="单位" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <div className={classNames({
                        "system-ope": true,
                    })} style={{ width: '20px' }} onClick={() => {
                        if (unit == "mm") {
                            setUnit("m");
                            if (engine) {
                                let unitRatio = Transfer2DisplayScale("m");
                                engine.registUnitRatio(1 / unitRatio);
                            }
                        } else {
                            setUnit("mm");
                            if (engine) {
                                let unitRatio = Transfer2DisplayScale("mm");
                                engine.registUnitRatio(1 / unitRatio);
                            }
                        }
                    }}>{unit}</div>
                </Tooltip>
                <div className={classNames({
                    "system-ope": true,
                    "active": lod
                })} onClick={() => { setLod(!lod) }}>lod</div>
                <Tooltip title="全局设置" color="#555555" mouseEnterDelay={0} mouseLeaveDelay={0} arrow={false}>
                    <div
                        className="system-ope"
                        style={{ width: "18px" }}
                    >
                        <SettingOutlined />
                    </div>
                </Tooltip>
            </div>

            {/* 对象捕捉菜单 */}
            <ContextMenu
                visible={captureMenuVisible}
                top={window.innerHeight - 30}
                left={captureMenuVisibleLeft - 120}
                mask
                onCancel={() => setCaptureMenuVisible(false)}
                onRightClick={() => setCaptureMenuVisible(false)}
                onEscape={() => {
                    setCaptureMenuVisible(false);
                }}
            >
                {CaptureMenu}
            </ContextMenu>
            {/* 设置弹窗 */}
            <Modal
                open={bottomModalVisible}
                showFooter={false}
                onCancel={() => setBottomModalVisible(false)}
                title='草图设置'
                width={450}>
                <BottomSetting
                    selectedTabPane={tabPane}
                    onCancel={() => { setBottomModalVisible(false); }}
                />
            </Modal>
        </div >
    )
}